// third party imports
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";

// internal rebet imports
import "./stylesTransactionTable.css";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../api/ApiRoutes";
import { handleExpiredTokenError } from "../../../../utils/ErrorUtils";
import useGetTransactionFilterDropdowns, {
  type TransactionFilterOption,
} from "../../../../queries/user-manager/useGetTransactionFilterDropdowns";

// component imports
import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";
import TransactionsFilters from "./TransactionsFilters";
import { PaymentDetailsDrawer } from "../../../shared";

import CustomDatePicker from "../../../CommonComponents/CustomDatePicker/CustomDatePicker";
import { KPI_CARD_BACKGROUND } from "../../../../utils/Constants";
import KpiCard from "../../../CommonComponents/Dashboard/KpiCard/KpiCard";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatDateNoTimezone,
  getAdminNow,
} from "../../../../utils/dateService";
import DataTableUntyped from "../../../shared/dashboards/DataTable/DataTable";
import useExternalTableFilters from "../../../shared/table-filters/useExternalTableFilters";
import {
  resolveDateRangeParams,
  toQueryString,
} from "../../../shared/table-filters/filterQueryUtils";

import type {
  Transaction,
  TransactionStatus,
  TransactionTotals,
  TransactionsApiResponse,
} from "../../../../types/transactions";
import {
  formatTransactionBalance,
  formatTransactionMoney,
  renderCardBankInfoCell,
  renderOptionalValue,
  renderPaymentTypeLabel,
  renderReasonCell,
  renderTransactionStatus,
} from "../shared/transactionColumnHelpers";

type TransactionTableProps = {
  userData: { user_id?: string; username?: string } | null | undefined;
  adminData: { timezone?: string } | null | undefined;
  currencySymbol?: string;
};

type TransactionFilters = {
  searchValue: string;
  // PAMENG-1936: holds URL-safe `value`s from the titles dropdown
  // (e.g. ["Cash_Deposit", "Cash_Withdraw"]). Empty array means
  // "no title filter" — the BE returns all transaction types.
  // The BE expects these comma-joined as `transaction_titles=...`.
  transactionTitles: string[];
  // Wallet/currency filter (BE: `wallet_type`). Single-select, drawn from
  // `dropdownOptions.currencies`. Empty string means "no currency filter"
  // — the BE returns transactions across all wallet types.
  moneyType: string;
};

type PaginationState = {
  current_page: number;
  page_size: number;
  total_results: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
};

type BetsCacheEntry = {
  all_bets: Transaction[];
  pagination: PaginationState;
};

// Snapshot of the filter values the BE is currently filtering by. Distinct
// from the draft `filters` state held by `useExternalTableFilters`, which
// tracks what the user is *editing* in the panel. Only Apply / Search /
// Clear / date-change commit the draft into this snapshot.
type AppliedFilterSnapshot = {
  searchValue: string;
  transactionTitles: string[];
  moneyType: string;
};

const EMPTY_APPLIED: AppliedFilterSnapshot = {
  searchValue: "",
  transactionTitles: [],
  moneyType: "",
};

type StoredToken = { AccessToken?: string } | null;

// useExternalTableFilters is JS without types; this typed view captures the
// portion of the surface this component uses.
type FiltersHookResult = {
  filters: TransactionFilters;
  normalizedFilters: TransactionFilters;
  setFilter: <K extends keyof TransactionFilters>(
    key: K,
    value: TransactionFilters[K],
  ) => void;
  resetFilters: () => void;
  hasActiveFilters: () => boolean;
};

// DataTable is a JS module without types; cast to an FC typed for the props
// this component uses. Mirrors the pattern in BetTable.tsx (TypedDataTable).
type TransactionsDataTableProps = {
  columns: ColumnsType<Transaction>;
  dataSource: Transaction[];
  pagination:
    | false
    | {
        current: number;
        pageSize: number;
        total: number;
        onChange: (page: number, size: number) => void;
        showSizeChanger?: boolean;
        pageSizeOptions?: readonly string[] | string[];
        showQuickJumper?: boolean;
        hideOnSinglePage?: boolean;
        itemRender?: (
          page: number,
          type: "page" | "prev" | "next" | "jump-prev" | "jump-next",
          originalElement: React.ReactNode,
        ) => React.ReactNode;
      };
  scrollPreset?: string;
  scrollOverrides?: { x?: number };
  rowKey?: (record: Transaction) => string | undefined;
  onRow?: (record: Transaction) => {
    onClick?: () => void;
    className?: string;
    style?: React.CSSProperties;
  };
};
const TypedDataTable =
  DataTableUntyped as unknown as React.FC<TransactionsDataTableProps>;

// Cursor-based pagination on the BE side
// (admin-panel-users/v1/{user_id}/transactions). The BE accepts `page_size`
// as a query param; this constant is the default the table opens with.
const DEFAULT_PAGE_SIZE = 20;
// Ant Design's built-in Table.pagination expects `pageSizeOptions` as strings.
const PAGE_SIZE_OPTIONS: readonly string[] = ["10", "20", "50", "100"];

// Stored-title values pre-selected on a fresh user load. Matched on values,
// not labels — labels localize per operator language.
// ("Prize Redemption" is a label-only sweepstakes rewrite of these values.)
const CASH_TITLE_DEFAULT_VALUES: ReadonlySet<string> = new Set([
  "cash_deposit",
  "cash_withdraw",
  "cash_withdrawal",
  // PAMENG-2535: Bitso penny-verification rows share the transactions list.
  // Pre-select them alongside the cash defaults (when the tenant-gated
  // dropdown option is present) so ops sees verification status without
  // clearing the default filter.
  "clabe_verification",
]);

const TransactionTable: React.FC<TransactionTableProps> = ({
  userData,
  adminData: _adminData,
  currencySymbol,
}) => {
  // non-state variables
  const rawToken = localStorage.getItem("token");
  const token: StoredToken = rawToken
    ? (JSON.parse(rawToken) as StoredToken)
    : null;
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken,
  );
  const navigate = useNavigate();
  const location = useLocation();

  // state variables
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<
    string | null | undefined
  >();
  const [isApiRunning, setIsApiRunning] = useState<boolean>(false);
  const [isKpisLoading, setIsKpisLoading] = useState<boolean>(true);
  const [transactions, setTransactions] = useState<Transaction[]>([]);

  const todayRange: [Dayjs, Dayjs] = [
    getAdminNow().startOf("day"),
    getAdminNow().endOf("day"),
  ];
  const [transactionDates, setTransactionDates] = useState<
    [Dayjs, Dayjs] | null
  >(null);
  const [summary, setSummary] = useState<TransactionTotals>({
    total_purchased: 0,
    total_withdrawn: 0,
  });
  // Cache totals from first page load to include in subsequent pagination requests
  const [cachedTotals, setCachedTotals] = useState<TransactionTotals | null>(
    null,
  );
  const [_resetTable, setResetTable] = useState<boolean>(false);
  const [betsCache, setBetsCache] = useState<Record<number, BetsCacheEntry>>(
    {},
  );

  const {
    filters,
    normalizedFilters,
    setFilter,
    resetFilters,
    hasActiveFilters,
  } = useExternalTableFilters({
    searchValue: "",
    transactionTitles: [],
    moneyType: "",
  }) as unknown as FiltersHookResult;

  // Filter values currently feeding the BE request. Updated only via
  // Apply / Search / Clear / date-change so the panel can stage edits
  // without triggering refetches on every keystroke or checkbox.
  const [appliedSearchValue, setAppliedSearchValue] = useState<string>("");
  const [appliedTransactionTitles, setAppliedTransactionTitles] = useState<
    string[]
  >([]);
  const [appliedMoneyType, setAppliedMoneyType] = useState<string>("");

  // Dropdown options for the title + currency filters. Cached via
  // react-query (5 min staleTime). Used only to populate the selects —
  // we no longer pre-filter on first load (the BE now returns all
  // transaction titles by default; users opt into filters via the
  // dropdowns).
  const { data: dropdownOptions, isPending: isDropdownPending } =
    useGetTransactionFilterDropdowns();

  const titleOptions = useMemo<TransactionFilterOption[]>(
    () => dropdownOptions?.titles ?? [],
    [dropdownOptions?.titles],
  );

  const currencyOptions = useMemo<TransactionFilterOption[]>(
    () => dropdownOptions?.currencies ?? [],
    [dropdownOptions?.currencies],
  );

  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [pagination, setPagination] = useState<PaginationState>({
    current_page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    total_results: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });

  const handleTransactionMethod = (record: Transaction): string | undefined => {
    // Manual-Add / Manual-Remove by_admin logic moved to BE
    return record?.title ?? undefined;
  };

  const [isPaymentDetailsOpen, setIsPaymentDetailsOpen] = useState(false);
  const [selectedPaymentTransactionId, setSelectedPaymentTransactionId] =
    useState<string | null>(null);

  const openPaymentDetails = (record: Transaction) => {
    setSelectedPaymentTransactionId(record?.transaction_id ?? null);
    setIsPaymentDetailsOpen(true);
  };

  const closePaymentDetails = () => {
    setIsPaymentDetailsOpen(false);
    setSelectedPaymentTransactionId(null);
  };

  const columns: ColumnsType<Transaction> = [
    {
      title: AppStrings.DATE_TIME,
      dataIndex: "timestamp",
      key: "timestamp",
      width: 150,
      render: (text: string) => (
        <p className="timestamp-text">
          {formatDateNoTimezone(text, "DD MMMM YYYY, HH:mm:ss")}
        </p>
      ),
    },
    {
      title: AppStrings.ID,
      dataIndex: "transaction_id",
      key: "transaction_id",
      width: 250,
    },
    {
      title: AppStrings.MONEY_TYPE,
      dataIndex: "wallet_type",
      key: "wallet_type",
      width: 150,
    },
    {
      title: AppStrings.AMOUNT,
      dataIndex: "amount",
      key: "amount",
      width: 100,
      render: (text: string | number | null, record: Transaction) =>
        formatTransactionMoney(text, record.wallet_type, currencySymbol),
    },
    {
      title: AppStrings.TYPE,
      dataIndex: "transaction_type",
      key: "transaction_type",
      width: 100,
    },
    {
      title: AppStrings.TRANSACTION_METHOD,
      dataIndex: "transaction_method",
      key: "transaction_method",
      width: 200,
      render: (_text: unknown, record: Transaction) => (
        <p
          className={`transaction-method-text ${
            record?.by_admin ? "by-admin" : "not-by-admin"
          }`}
        >
          {handleTransactionMethod(record)}
        </p>
      ),
    },
    {
      title: AppStrings.PAYMENT_GATEWAY,
      dataIndex: "payment_gateway",
      key: "payment_gateway",
      width: 180,
      render: (text: string | null | undefined) => renderOptionalValue(text),
    },
    {
      title: AppStrings.PAYMENT_TYPE,
      dataIndex: "payment_type",
      key: "payment_type",
      width: 180,
      render: (_text: unknown, record: Transaction) =>
        renderPaymentTypeLabel(record),
    },
    {
      title: AppStrings.CARD_BANK_INFO,
      dataIndex: "card_bank_info",
      key: "card_bank_info",
      width: 180,
      render: (text: string | null | undefined, record: Transaction) =>
        renderCardBankInfoCell(text, record),
    },
    {
      title: AppStrings.REASON,
      dataIndex: "reason",
      key: "reason",
      width: 150,
      render: (text: string | null | undefined, record: Transaction) =>
        renderReasonCell(text, record),
    },
    {
      title: AppStrings.BALANCE,
      dataIndex: "total_balance",
      key: "total_balance",
      width: 150,
      render: (text: string | number | null, record: Transaction) =>
        formatTransactionBalance(text, record, currencySymbol),
    },
    {
      title: AppStrings.STATUS,
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (text: TransactionStatus) => renderTransactionStatus(text),
    },
  ];

  // Pre-filter to Cash Deposit/Withdrawal + the user's primary currency
  // on every fresh user load. Primary currency = first option from the BE
  // dropdown (no per-user field exists). Ref tracks "applied for this
  // user" so re-renders don't re-fire.
  const initialLoadUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    initialLoadUserIdRef.current = null;
  }, [userData?.user_id]);

  useEffect(() => {
    const userId = userData?.user_id;
    if (!userId) return;
    // Wait for the dropdown query to settle so we have the title/currency
    // options to derive defaults from. If it errors out, isPending still
    // flips to false and we proceed with empty options → unfiltered fetch.
    if (isDropdownPending) return;
    if (initialLoadUserIdRef.current === userId) return;
    initialLoadUserIdRef.current = userId;

    // Consolidated options (PAMENG-2245) comma-join member values, so match
    // on components; the full opt.value round-trips to the BE filter.
    const titleDefaults = titleOptions
      .filter((opt) =>
        opt.value
          .split(",")
          .some((v) => CASH_TITLE_DEFAULT_VALUES.has(v.trim().toLowerCase())),
      )
      .map((opt) => opt.value);
    const moneyDefault = currencyOptions[0]?.value ?? "";

    setFilter("transactionTitles", titleDefaults);
    setFilter("moneyType", moneyDefault);
    setAppliedTransactionTitles(titleDefaults);
    setAppliedMoneyType(moneyDefault);
    setCachedTotals(null);
    setIsKpisLoading(true);
    setBetsCache({});

    void getUsersTransactions(1, true, todayRange, false, true, null, {
      searchValue: "",
      transactionTitles: titleDefaults,
      moneyType: moneyDefault,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.user_id, isDropdownPending, titleOptions, currencyOptions]);

  // api functions
  const getUsersTransactions = async (
    page: number = 1,
    refresh: boolean = false,
    queryString: [Dayjs, Dayjs] | string = todayRange,
    isQueryString: boolean = true,
    allTime: boolean = false,
    lastEvaluatedKeyArg: string | null = null,
    // Explicit applied snapshot — pass when a handler has just committed
    // new filter values, since the closure still sees the previous
    // applied* state until the next render.
    appliedOverride?: AppliedFilterSnapshot,
    // Explicit page-size override — used by handlePageSizeChange because
    // setPageSize is async and the state closure still holds the old size.
    pageSizeOverride?: number,
  ): Promise<void> => {
    setIsApiRunning(true);

    const applied: AppliedFilterSnapshot = appliedOverride ?? {
      searchValue: appliedSearchValue,
      transactionTitles: appliedTransactionTitles,
      moneyType: appliedMoneyType,
    };
    const effectivePageSize = pageSizeOverride ?? pageSize;

    const { startDate, endDate } = resolveDateRangeParams({
      queryString,
      isQueryString,
      allTime,
    });

    const cacheKey = page;

    if (betsCache[cacheKey] && !refresh) {
      const cached = betsCache[cacheKey];
      setTransactions(cached.all_bets);
      setPagination(cached.pagination);
      setIsApiRunning(false);
      return;
    }

    // Mutable param bag — keys are added conditionally below.
    const params: Record<string, string | number | undefined> = {
      page,
      page_size: effectivePageSize,
      user_id: userData?.user_id,
      start_date: startDate ?? "",
      end_date: endDate ?? "",
      last_evaluated_key: lastEvaluatedKeyArg ?? "",
    };

    // Include cached totals in subsequent page requests to prevent BE recalculation
    if (page > 1 && cachedTotals && !refresh) {
      params.totals = JSON.stringify(cachedTotals);
    }

    // Filter params come exclusively from the applied snapshot — drafts
    // in the filter panel don't reach the wire until the user clicks
    // Apply / Search.
    if (applied.searchValue) {
      params.transaction_id = applied.searchValue;
    }

    if (applied.transactionTitles.length > 0) {
      params.transaction_titles = applied.transactionTitles.join(",");
    }

    if (applied.moneyType) {
      params.wallet_type = applied.moneyType;
    }

    const queryStringParams = toQueryString(params, { includeEmpty: true });

    try {
      const response = (await PrivateAdminPanelInstance?.get(
        API_ROUTES?.GET_TRANSACTION_HISTORY?.replace(
          "{user_id}",
          params?.user_id != null ? String(params.user_id) : "",
        ) + queryStringParams,
      )) as TransactionsApiResponse | undefined;

      const response_data = response?.data?.data;

      const all_transactions: Transaction[] = Array.isArray(
        response?.data?.data?.items,
      )
        ? [...(response?.data?.data?.items ?? [])]
        : [];

      const all_transactions_sorted = all_transactions.sort(
        (a, b) =>
          parseFloat(String(b.timestamp)) - parseFloat(String(a.timestamp)),
      );

      const all_unique_transactions_sorted: Transaction[] = Array.from(
        new Map(
          all_transactions_sorted.map((transaction) => [
            transaction.transaction_id,
            transaction,
          ]),
        ).values(),
      );

      const nextLastEvaluatedKey = response?.data?.data?.last_evaluated_key;
      const hasNext = !!nextLastEvaluatedKey;
      const currentPage = page;
      const hasPrev = currentPage > 1;

      // Only update summary/KPIs on first page load or when refresh is true (date filter change)
      // This prevents KPIs from being recalculated on every page
      const totalsFromResponse = response_data?.totals;
      if ((page === 1 || refresh) && totalsFromResponse) {
        setSummary({
          total_purchased: totalsFromResponse.total_purchased,
          total_withdrawn: totalsFromResponse.total_withdrawn,
        });
        setCachedTotals(totalsFromResponse);
        setIsKpisLoading(false);
      }

      // Pagination total: prefer the filtered count (`pagination_total`,
      // present when a narrowing filter like `wallet_type` is active),
      // fall back to the unfiltered `total_transactions_count`. On pages
      // 2+ the BE may omit `totals`, so read from `cachedTotals` (captured
      // on page 1). If neither the response nor the cache has a count
      // (e.g. BE still on the old contract), estimate from the cursor.
      const authoritativeTotals = totalsFromResponse ?? cachedTotals;
      const paginationTotalFromBE =
        authoritativeTotals?.pagination_total ??
        authoritativeTotals?.total_transactions_count;

      const itemsOnThisPage = all_unique_transactions_sorted.length;
      const totalResults =
        paginationTotalFromBE ??
        (nextLastEvaluatedKey
          ? page * effectivePageSize + 1
          : (page - 1) * effectivePageSize + itemsOnThisPage);

      const paginationData: PaginationState = {
        current_page: currentPage,
        page_size: effectivePageSize,
        total_results: totalResults,
        total_pages: response?.data?.data?.pagination?.total_pages ?? 0,
        has_next: hasNext,
        has_prev: hasPrev,
      };

      setTransactions(all_unique_transactions_sorted);
      setLastEvaluatedKey(nextLastEvaluatedKey);
      setPagination(paginationData);

      setBetsCache((prev) => ({
        ...prev,
        [cacheKey]: {
          all_bets: all_unique_transactions_sorted,
          pagination: paginationData,
        },
      }));

      setIsApiRunning(false);
    } catch (error: unknown) {
      console.error("error fetching user transaction history", error);
      setIsApiRunning(false);
      const errResponse = (error as { response?: unknown } | undefined)
        ?.response;
      handleExpiredTokenError(navigate, errResponse);
    }
  };

  // helper functions

  // Resolve the date-range args to pass into `getUsersTransactions` based
  // on the current `transactionDates` selection. Centralised so handlers
  // don't keep open-coding the same allTime / tuple shuffle.
  const resolveActiveDateRangeArgs = (): {
    queryString: [Dayjs, Dayjs];
    isQueryString: boolean;
    allTime: boolean;
  } => ({
    queryString: transactionDates ?? todayRange,
    isQueryString: false,
    allTime: !transactionDates,
  });

  const handleDateChange = (queryString: string): void => {
    // Date change also wipes any committed filters — a "Last 7 days"
    // switch shouldn't silently keep a previously applied wallet_type
    // filter alive in the next response.
    setCachedTotals(null);
    setIsKpisLoading(true);
    resetFilters();
    setAppliedSearchValue("");
    setAppliedTransactionTitles([]);
    setAppliedMoneyType("");
    // A date change yields a different dataset — wipe the page-keyed
    // cache so paginating away and back doesn't render rows from the
    // previous range.
    setBetsCache({});
    void getUsersTransactions(
      1,
      true,
      queryString,
      true,
      false,
      null,
      EMPTY_APPLIED,
    );
  };

  const handleChangeTransactionTitles = (value: string[] | undefined): void => {
    setFilter("transactionTitles", Array.isArray(value) ? value : []);
  };

  const handleChangeMoneyType = (value: string | undefined): void => {
    setFilter("moneyType", value ?? "");
  };

  const handleSearchValueChange = (
    event: ChangeEvent<HTMLInputElement>,
  ): void => {
    setFilter("searchValue", event.target.value);
  };

  const handleApplyFilters = (): void => {
    // Mirror BetTable: applying the panel filters wipes any search-by-id
    // so the filtered list isn't pinned to a stale transaction id.
    const next: AppliedFilterSnapshot = {
      searchValue: "",
      transactionTitles: normalizedFilters.transactionTitles ?? [],
      moneyType: normalizedFilters.moneyType ?? "",
    };
    setAppliedSearchValue("");
    setAppliedTransactionTitles(next.transactionTitles);
    setAppliedMoneyType(next.moneyType);
    setFilter("searchValue", "");
    setCachedTotals(null);
    setIsKpisLoading(true);
    setBetsCache({});
    const { queryString, isQueryString, allTime } =
      resolveActiveDateRangeArgs();
    void getUsersTransactions(
      1,
      true,
      queryString,
      isQueryString,
      allTime,
      null,
      next,
    );
  };

  const handleSearchPressed = (): void => {
    const trimmedSearchValue = (normalizedFilters.searchValue ?? "").trim();
    // Nothing to search by — bail rather than fire a redundant request.
    if (!trimmedSearchValue) {
      return;
    }
    const next: AppliedFilterSnapshot = {
      searchValue: trimmedSearchValue,
      transactionTitles: appliedTransactionTitles,
      moneyType: appliedMoneyType,
    };
    setAppliedSearchValue(trimmedSearchValue);
    setCachedTotals(null);
    setIsKpisLoading(true);
    setBetsCache({});
    const { queryString, isQueryString, allTime } =
      resolveActiveDateRangeArgs();
    void getUsersTransactions(
      1,
      true,
      queryString,
      isQueryString,
      allTime,
      null,
      next,
    );
  };

  // Auto-fire single-transaction search when arriving with ?txnId from the
  // Users page. Strips txnId from the URL after firing.
  const autoSearchFiredRef = useRef(false);
  useEffect(() => {
    autoSearchFiredRef.current = false;
  }, [userData?.user_id]);
  useEffect(() => {
    if (autoSearchFiredRef.current) return;
    if (!userData?.user_id) return;
    const params = new URLSearchParams(location.search);
    const txnId = params.get("txnId");
    if (!txnId) return;
    autoSearchFiredRef.current = true;

    setFilter("searchValue", txnId);
    const next: AppliedFilterSnapshot = {
      searchValue: txnId,
      transactionTitles: appliedTransactionTitles,
      moneyType: appliedMoneyType,
    };
    setAppliedSearchValue(txnId);
    setCachedTotals(null);
    setIsKpisLoading(true);
    setBetsCache({});
    const { queryString, isQueryString, allTime } =
      resolveActiveDateRangeArgs();
    void getUsersTransactions(
      1,
      true,
      queryString,
      isQueryString,
      allTime,
      null,
      next,
    );

    params.delete("txnId");
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true },
    );
    // Effect closes over getUsersTransactions and resolveActiveDateRangeArgs,
    // which are recreated on every render. Listing them in deps would cause
    // the effect to re-fire on every render — the autoSearchFiredRef guard
    // short-circuits the body so behavior is correct, but lint flags the
    // unstable refs. Wrapping both in useCallback solves the warning but
    // pulls the rest of the component's closure web into refactor scope.
    // The ref reset above handles user_id changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.user_id, location.search]);

  const handleSearchClear = (): void => {
    // Draft is already wiped by handleSearchValueChange. Only refetch when
    // there is an applied search to drop — otherwise this would be a redundant
    // request firing on every × click.
    if (!appliedSearchValue) {
      return;
    }
    const next: AppliedFilterSnapshot = {
      searchValue: "",
      transactionTitles: appliedTransactionTitles,
      moneyType: appliedMoneyType,
    };
    setAppliedSearchValue("");
    setCachedTotals(null);
    setIsKpisLoading(true);
    setBetsCache({});
    const { queryString, isQueryString, allTime } =
      resolveActiveDateRangeArgs();
    void getUsersTransactions(
      1,
      true,
      queryString,
      isQueryString,
      allTime,
      null,
      next,
    );
  };

  const handleClearFilters = (): void => {
    resetFilters();
    setAppliedSearchValue("");
    setAppliedTransactionTitles([]);
    setAppliedMoneyType("");
    setCachedTotals(null);
    setIsKpisLoading(true);
    setBetsCache({});
    setTransactionDates(null);
    setResetTable(false);
    void getUsersTransactions(
      1,
      true,
      todayRange,
      false,
      true,
      null,
      EMPTY_APPLIED,
    );
  };

  const handlePageSizeChange = (nextPageSize: number): void => {
    // Guard against re-entrant calls with the same size (e.g. the select
    // firing on remount) — noop keeps the cache warm and avoids a
    // superfluous refetch.
    if (nextPageSize === pageSize) return;

    setPageSize(nextPageSize);
    // Page-keyed cache assumes a fixed page_size, so changing size must
    // invalidate every cached page. Cursor state also resets — the
    // previous `last_evaluated_key` was scoped to the old size.
    setBetsCache({});
    setLastEvaluatedKey(null);
    setCachedTotals(null);
    setIsKpisLoading(true);

    const { queryString, isQueryString, allTime } =
      resolveActiveDateRangeArgs();
    const applied: AppliedFilterSnapshot = {
      searchValue: appliedSearchValue,
      transactionTitles: appliedTransactionTitles,
      moneyType: appliedMoneyType,
    };
    // Force page 1 so the cursor walk restarts under the new page size.
    // `pageSizeOverride` is required because setPageSize is async — the
    // getUsersTransactions closure still reads the previous state.
    void getUsersTransactions(
      1,
      true,
      queryString,
      isQueryString,
      allTime,
      null,
      applied,
      nextPageSize,
    );
  };

  // Ant Design's Table.pagination.onChange also fires when the user
  // switches page size — in that case `nextSize` differs from the
  // current state. Route that through handlePageSizeChange so the cursor
  // map, totals cache, and BE param stay consistent.
  const handlePaginationChange = (
    nextPage: number,
    nextSize: number,
  ): void => {
    if (nextSize !== pageSize) {
      handlePageSizeChange(nextSize);
      return;
    }
    const { queryString, isQueryString, allTime } =
      resolveActiveDateRangeArgs();

    void getUsersTransactions(
      nextPage,
      false,
      queryString,
      isQueryString,
      allTime,
      lastEvaluatedKey ?? null,
    );
  };

  const headerText = AppStrings.USERNAME_TRANSACTIONS?.replace(
    "{username}",
    userData?.username ?? "",
  );

  const hasAppliedOrDraftFilters =
    hasActiveFilters() ||
    Boolean(appliedSearchValue) ||
    appliedTransactionTitles.length > 0 ||
    Boolean(appliedMoneyType);

  return (
    <div className="transactions-user-wallet-container">
      <PaymentDetailsDrawer
        open={isPaymentDetailsOpen}
        onClose={closePaymentDetails}
        userId={userData?.user_id}
        transactionId={selectedPaymentTransactionId}
        currencySymbol={currencySymbol}
      />
      <div className="transactions-user-wallet-header">
        <div className="kpi-cards-container">
          <KpiCard
            isApiRunning={isKpisLoading}
            title={AppStrings.TOTAL_PURCHASED}
            value={summary?.total_purchased}
            hugeValue={false}
            percentage={false}
            currencySymbol={currencySymbol}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            isApiRunning={isKpisLoading}
            title={AppStrings.TOTAL_WITHDRAWN}
            value={summary?.total_withdrawn}
            hugeValue={false}
            percentage={false}
            currencySymbol={currencySymbol}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            isApiRunning={isKpisLoading}
            title={AppStrings.REVENUE}
            value={
              (summary?.total_purchased ?? 0) - (summary?.total_withdrawn ?? 0)
            }
            titleTooltip={AppStrings.REVENUE_KPI_TOOLTIP}
            hugeValue={false}
            percentage={false}
            currencySymbol={currencySymbol}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
        </div>
        <CustomDatePicker
          getData={handleDateChange}
          dates={transactionDates}
          setDates={setTransactionDates}
          showPresets={true}
          startRange={AppStrings.TODAY}
        />
      </div>
      <div className="transactions-user-wallet-table-container">
        <TransactionsFilters
          headerText={headerText}
          titleOptions={titleOptions}
          currencyOptions={currencyOptions}
          isDropdownLoading={isDropdownPending}
          transactionTitles={filters.transactionTitles}
          moneyType={filters.moneyType}
          searchValue={filters.searchValue}
          onTransactionTitlesChange={handleChangeTransactionTitles}
          onMoneyTypeChange={handleChangeMoneyType}
          onSearchValueChange={handleSearchValueChange}
          onSearchClear={handleSearchClear}
          onSearchPressed={handleSearchPressed}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          hasAppliedOrDraftFilters={hasAppliedOrDraftFilters}
        />

        <div className="transactions-table-container">
          {isApiRunning ? (
            <div className="loading-container">
              <RebetLoader height={"100px"} width={"100px"} />
            </div>
          ) : (
            <TypedDataTable
              columns={columns}
              dataSource={transactions}
              scrollPreset="default"
              scrollOverrides={{ x: 2050 }}
              rowKey={(record) => record?.transaction_id}
              onRow={(record) =>
                // PAMENG-2535: verification rows aren't served by the
                // payment-details endpoint — their details live in the
                // Card/Bank Info popover instead, so don't open the drawer.
                record?.is_verification
                  ? {}
                  : {
                      onClick: () => openPaymentDetails(record),
                      className: "transaction-table-row-clickable",
                      style: { cursor: "pointer" },
                    }
              }
              pagination={
                pagination.total_results > pageSize
                  ? {
                      current: pagination.current_page,
                      // Local state wins over the BE echo: the dropdown
                      // must stay pinned to the admin's selection even if
                      // the BE ignores the `page_size` param (old contract).
                      pageSize,
                      total: pagination.total_results,
                      onChange: (nextPage, nextSize) =>
                        handlePaginationChange(nextPage, nextSize),
                      showSizeChanger: true,
                      pageSizeOptions: [...PAGE_SIZE_OPTIONS],
                      showQuickJumper: false,
                      hideOnSinglePage: true,
                      itemRender: (p, type, originalElement) => {
                        // Cursor-based paging: only ±1 from the current
                        // page is reachable, so render the other numbered
                        // pages disabled and hide the "..." jumpers.
                        if (type === "page") {
                          const isDisabled =
                            Math.abs(p - pagination.current_page) > 1;
                          return isDisabled ? (
                            <span
                              style={{ opacity: 0.4, pointerEvents: "none" }}
                            >
                              {p}
                            </span>
                          ) : (
                            originalElement
                          );
                        }
                        if (type === "jump-prev" || type === "jump-next")
                          return null;
                        return originalElement;
                      },
                    }
                  : false
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionTable;
